import { useEffect, useId, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { useUserContext } from '../../../contexts/UserContext';
import { authClient } from '../../../utils/amplifyClient';
import {
    updateBusinessAdmin as updateBusinessAdminMutation,
    updateCompanyOwner as updateCompanyOwnerMutation,
    updateUser as updateUserMutation,
} from '../../../graphql/mutations';
import { formatPhoneNumber, stripNonDigits } from '../../../utils/phoneFormat';
import { S3_IMAGE_PATH } from '../../../utils/constants';
import { deleteImageFromS3, resizeFile } from '../../../utils/helperFunctions';
import { BusinessAdmin, CompanyOwner, User } from '../../../types/schema';
import { Btn } from '../../ui';

interface ProfileForm {
    firstName: string;
    lastName: string;
    phone: string;
}

const MAX_PHOTO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/jpg']);

const splitName = (name: string): { firstName: string; lastName: string } => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    return {
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' '),
    };
};

export const MyProfileSection = () => {
    const {
        companyOwner,
        companyUser,
        setCompanyOwner,
        setCompanyUser,
        businessAdmin,
        setBusinessAdmin,
        allCompanyUsers,
        setAllCompanyUsers,
    } = useUserContext();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [photoUrl, setPhotoUrl] = useState('');
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [photoError, setPhotoError] = useState<string | null>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const photoInputId = useId();
    const photoLabelId = useId();
    const photoHintId = useId();
    const photoStatusId = useId();
    const emailHintId = useId();
    const sectionTitleId = useId();
    const roleFieldId = useId();

    // A BusinessAdmin (platform staff) has no CompanyOwner/User record. The company
    // profile fields (phone, photo) do not apply; name + email come from BusinessAdmin.
    const isBusinessAdminProfile = !companyOwner && !companyUser && Boolean(businessAdmin);
    const hasEditableProfile = Boolean(companyOwner?.id || companyUser?.id || businessAdmin?.id);

    const user = companyOwner || companyUser || businessAdmin;
    const name = user?.name || '';
    const email = user?.email || '';
    const initials = name ? name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
    const storedPhone = isBusinessAdminProfile
        ? ''
        : stripNonDigits((user as CompanyOwner | User | null)?.contactPhoneNumber as string || '');
    const role = companyOwner
        ? 'Owner / Administrator'
        : businessAdmin
            ? (businessAdmin.role || 'Site Admin')
            : (companyUser?.role || 'User');
    const storedPhotoPath = (companyUser?.userPhoto as string) || '';
    const companyId =
        (companyUser?.companyId as string) || (companyOwner?.companyId as string) || '';

    const [form, setForm] = useState<ProfileForm>(() => ({
        ...splitName(name),
        phone: storedPhone,
    }));

    // Re-sync from context while not editing so an external save is reflected here.
    useEffect(() => {
        if (isEditing) return;
        setForm({ ...splitName(name), phone: storedPhone });
    }, [name, storedPhone, isEditing]);

    useEffect(() => {
        let isCancelled = false;

        if (!storedPhotoPath) {
            setPhotoUrl('');
            return;
        }

        (async () => {
            try {
                const result = await getUrl({ path: storedPhotoPath });
                if (!isCancelled) {
                    setPhotoUrl(result.url.href);
                }
            } catch (error) {
                console.error('Error loading profile photo:', error);
                if (!isCancelled) {
                    setPhotoUrl('');
                }
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [storedPhotoPath]);

    const patchUserPhotoInContext = (userPhoto: string) => {
        if (companyUser?.id) {
            setCompanyUser({ ...companyUser, userPhoto } as User);
        }

        const savedIds = new Set([companyUser?.id].filter(Boolean) as string[]);
        if (savedIds.size > 0 && allCompanyUsers.length > 0) {
            setAllCompanyUsers(
                allCompanyUsers.map((row) =>
                    savedIds.has(row.id) ? ({ ...row, userPhoto } as User) : row,
                ),
            );
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            setForm({ ...splitName(name), phone: storedPhone });
            setSaveError(null);
        }
        setIsEditing(!isEditing);
    };

    const openPhotoPicker = () => {
        if (isUploadingPhoto) return;
        setPhotoError(null);
        photoInputRef.current?.click();
    };

    const handlePhotoSelect = async (file: File) => {
        if (!companyUser?.id) {
            setPhotoError('Profile photos require a user account record. Try again after setup completes.');
            return;
        }
        if (!companyId) {
            setPhotoError('Company is not set up yet, so photos cannot be saved.');
            return;
        }

        const type = (file.type || '').toLowerCase();
        const looksLikeAccepted =
            ACCEPTED_PHOTO_TYPES.has(type) ||
            /\.(jpe?g|png)$/i.test(file.name);
        if (!looksLikeAccepted) {
            setPhotoError('Please choose a JPG or PNG image.');
            return;
        }
        if (file.size > MAX_PHOTO_BYTES) {
            setPhotoError('Photo must be 2MB or smaller.');
            return;
        }

        setIsUploadingPhoto(true);
        setPhotoError(null);

        try {
            const resizedImage = await resizeFile(file);
            const imageFile = resizedImage as File;
            const safeName = (name || 'user').replace(/\s+/g, '-');
            const fileName = `${uuidv4()}-USER-${safeName}-${imageFile.name}`;
            const savedImage = await uploadData({
                path: S3_IMAGE_PATH.userPhotoPath(companyId, companyUser.id, fileName),
                data: imageFile,
            }).result;

            await authClient.graphql({
                query: updateUserMutation,
                variables: {
                    input: {
                        id: companyUser.id,
                        userPhoto: savedImage.path,
                    },
                },
            });

            const previousPath = storedPhotoPath;
            patchUserPhotoInContext(savedImage.path);

            if (previousPath && previousPath !== savedImage.path) {
                try {
                    await deleteImageFromS3(previousPath);
                } catch {
                    // Non-blocking: old object may already be gone.
                }
            }
        } catch (error) {
            console.error('Failed to upload profile photo:', error);
            Sentry.captureException(error);
            setPhotoError('Could not upload your photo. Please try again.');
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handlePhotoRemove = async () => {
        if (!companyUser?.id || !storedPhotoPath || isUploadingPhoto) return;

        setIsUploadingPhoto(true);
        setPhotoError(null);

        try {
            await authClient.graphql({
                query: updateUserMutation,
                variables: {
                    input: {
                        id: companyUser.id,
                        userPhoto: '',
                    },
                },
            });

            patchUserPhotoInContext('');

            try {
                await deleteImageFromS3(storedPhotoPath);
            } catch {
                // Non-blocking cleanup.
            }
        } catch (error) {
            console.error('Failed to remove profile photo:', error);
            Sentry.captureException(error);
            setPhotoError('Could not remove your photo. Please try again.');
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const handleSave = async () => {
        const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
        if (!fullName) {
            setSaveError('First name is required.');
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        const updates = { name: fullName, contactPhoneNumber: form.phone };

        try {
            // BusinessAdmin (platform staff) has no CompanyOwner/User row — only a name
            // is editable, via the BusinessAdmin record.
            if (isBusinessAdminProfile && businessAdmin?.id) {
                await authClient.graphql({
                    query: updateBusinessAdminMutation,
                    variables: { input: { id: businessAdmin.id, name: fullName } },
                });
                setBusinessAdmin({ ...businessAdmin, name: fullName } as BusinessAdmin);
                setIsEditing(false);
                return;
            }

            if (companyOwner?.id) {
                await authClient.graphql({
                    query: updateCompanyOwnerMutation,
                    variables: { input: { id: companyOwner.id, ...updates } },
                });
                setCompanyOwner({ ...companyOwner, ...updates } as CompanyOwner);
            }

            // Owners also have a mirrored User row (same id) — keep both in sync so the
            // name/phone match wherever the app reads the User record.
            if (companyUser?.id) {
                await authClient.graphql({
                    query: updateUserMutation,
                    variables: { input: { id: companyUser.id, ...updates } },
                });
                setCompanyUser({ ...companyUser, ...updates } as User);
            }

            if (!companyOwner?.id && !companyUser?.id) {
                // No editable profile record. Surface a message instead of throwing so this
                // never becomes an unhandled Sentry error (see /settings for platform staff).
                setSaveError('There is no editable profile for this account.');
                return;
            }

            // /users and /users/:userId read from this cached directory, so patch it here
            // instead of waiting for the next full reload.
            const savedIds = new Set([companyOwner?.id, companyUser?.id].filter(Boolean) as string[]);
            if (savedIds.size > 0 && allCompanyUsers.length > 0) {
                setAllCompanyUsers(
                    allCompanyUsers.map((row) =>
                        savedIds.has(row.id) ? ({ ...row, ...updates } as User) : row,
                    ),
                );
            }

            setIsEditing(false);
        } catch (error) {
            console.error('Failed to save profile:', error);
            Sentry.captureException(error);
            setSaveError('Could not save your profile. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const photoUploadLabel = photoUrl ? 'Change profile photo' : 'Upload profile photo';
    const photoDescribedBy = [
        photoHintId,
        isUploadingPhoto ? photoStatusId : null,
        photoError ? `${photoStatusId}-error` : null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <section className="settings-section" aria-labelledby={sectionTitleId}>
            <div className="settings-section-header">
                <div>
                    <h2 id={sectionTitleId} className="settings-section-title">
                        My Profile
                    </h2>
                    <p className="settings-section-subtitle">Personal account details and password</p>
                </div>
                <div className="settings-section-actions">
                    {isEditing && (
                        <Btn
                            variant="primary"
                            buttonSize="sm"
                            buttonText={isSaving ? 'Saving…' : 'Save'}
                            buttonDisabled={isSaving}
                            buttonOnClick={handleSave}
                            ariaLabel={isSaving ? 'Saving profile' : 'Save profile'}
                        />
                    )}
                    {hasEditableProfile && (
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText={isEditing ? 'Cancel' : 'Edit'}
                            buttonDisabled={isSaving}
                            buttonOnClick={handleEditToggle}
                            ariaLabel={isEditing ? 'Cancel profile edits' : 'Edit profile'}
                        />
                    )}
                </div>
            </div>

            <div className="settings-section-body">
                {saveError && (
                    <p className="settings-form-error" role="alert">
                        {saveError}
                    </p>
                )}

                {/* Profile Photo — company profiles only (BusinessAdmin has no photo) */}
                {!isBusinessAdminProfile && (
                <div className="profile-photo-row">
                    {photoUrl ? (
                        <img
                            className="profile-photo-image"
                            src={photoUrl}
                            alt={name ? `${name} profile photo` : 'Profile photo'}
                            onError={() => setPhotoUrl('')}
                        />
                    ) : (
                        <div className="profile-photo-placeholder" aria-hidden="true">
                            {initials}
                        </div>
                    )}
                    <div className="profile-photo-info">
                        <label id={photoLabelId} htmlFor={photoInputId} className="profile-photo-label">
                            Profile Photo
                        </label>
                        <span id={photoHintId} className="profile-photo-hint">
                            JPG or PNG, Max 2MB
                        </span>
                        <div className="profile-photo-actions">
                            <Btn
                                variant="primary"
                                buttonSize="sm"
                                buttonText={isUploadingPhoto ? 'Uploading…' : photoUrl ? 'Change' : 'Upload'}
                                buttonDisabled={isUploadingPhoto}
                                buttonOnClick={openPhotoPicker}
                                ariaLabel={photoUploadLabel}
                                buttonAriaProps={{
                                    'aria-controls': photoInputId,
                                    'aria-describedby': photoDescribedBy,
                                }}
                            />
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonText="Remove"
                                buttonDisabled={isUploadingPhoto || !storedPhotoPath}
                                buttonOnClick={handlePhotoRemove}
                                ariaLabel="Remove profile photo"
                            />
                        </div>
                        {isUploadingPhoto && (
                            <span id={photoStatusId} className="profile-photo-status" role="status" aria-live="polite">
                                Uploading profile photo…
                            </span>
                        )}
                        {photoError && (
                            <p
                                id={`${photoStatusId}-error`}
                                className="settings-form-error profile-photo-error"
                                role="alert"
                            >
                                {photoError}
                            </p>
                        )}
                    </div>
                    <input
                        ref={photoInputRef}
                        id={photoInputId}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                        className="profile-photo-input"
                        disabled={isUploadingPhoto}
                        aria-labelledby={photoLabelId}
                        aria-describedby={photoDescribedBy}
                        tabIndex={-1}
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                void handlePhotoSelect(file);
                            }
                            event.target.value = '';
                        }}
                    />
                </div>
                )}

                {/* Fields */}
                <div className="settings-field-grid">
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-first-name">
                            First Name
                        </label>
                        <input
                            id="settings-first-name"
                            type="text"
                            className="settings-field-input"
                            value={form.firstName}
                            readOnly={!isEditing}
                            disabled={!isEditing}
                            autoComplete="given-name"
                            onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-last-name">
                            Last Name
                        </label>
                        <input
                            id="settings-last-name"
                            type="text"
                            className="settings-field-input"
                            value={form.lastName}
                            readOnly={!isEditing}
                            disabled={!isEditing}
                            autoComplete="family-name"
                            onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                        />
                    </div>
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-email">
                            Email Address
                        </label>
                        <input
                            id="settings-email"
                            type="email"
                            className="settings-field-input"
                            value={email}
                            readOnly
                            disabled
                            aria-describedby={emailHintId}
                        />
                        <span id={emailHintId} className="settings-field-hint">
                            Email is your login. Contact support to change.
                        </span>
                    </div>
                    {!isBusinessAdminProfile && (
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor="settings-phone">
                            Phone Number
                        </label>
                        <input
                            id="settings-phone"
                            type="tel"
                            inputMode="numeric"
                            className="settings-field-input"
                            value={formatPhoneNumber(form.phone)}
                            placeholder={isEditing ? '(xxx) xxx-xxxx' : undefined}
                            readOnly={!isEditing}
                            disabled={!isEditing}
                            autoComplete="tel-national"
                            onChange={(e) =>
                                setForm((prev) => ({ ...prev, phone: stripNonDigits(e.target.value).slice(0, 10) }))
                            }
                        />
                    </div>
                    )}
                    <div className="settings-field">
                        <label className="settings-field-label" htmlFor={roleFieldId}>
                            Role
                        </label>
                        <input
                            id={roleFieldId}
                            type="text"
                            className="settings-field-input"
                            value={role}
                            readOnly
                            disabled
                        />
                    </div>
                    <div className="settings-field">
                        <span className="settings-field-label">Password</span>
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonText="Change Password"
                            buttonDisabled
                            ariaLabel="Change password (not available yet)"
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};
